import type {
  SmsPurpose,
  SmsRoutine,
  SmsRoutineSettings,
  SmsRoutineSpec,
  SmsTemplate,
  SmsVariable,
} from './smsTypes.ts';

export const PURPOSE_LABELS: Readonly<Record<SmsPurpose, string>> = {
  transactional: 'تراکنشی',
  service: 'خدماتی و یادآوری',
  marketing: 'تبلیغاتی',
};

export const OCCASION_LABELS = {
  birthday: 'تولد مشتری',
  nowruz: 'نوروز',
  yalda: 'شب یلدا',
} as const;

export const ORDER_EVENT_LABELS = {
  order_confirmed: 'پس از ثبت سفارش',
  payment_received: 'پس از دریافت وجه',
} as const;

const faDigits = '۰۱۲۳۴۵۶۷۸۹';
export const faNum = (n: number): string => String(n).replace(/\d/g, (d) => faDigits[Number(d)]!);

const VARIABLE = /\{([A-Za-z][A-Za-z0-9]*)\}/g;

/** Fill `{variable}`s with the catalog's sample values; unknown ones stay visible. */
export function previewBody(body: string, variables: readonly SmsVariable[]): string {
  const samples = new Map(variables.map((v) => [v.key, v.sample]));
  return body.replace(VARIABLE, (whole, key: string) => samples.get(key) ?? whole);
}

export function bodyVariables(body: string): readonly string[] {
  const keys: string[] = [];
  for (const m of body.matchAll(VARIABLE)) if (!keys.includes(m[1]!)) keys.push(m[1]!);
  return keys;
}

export interface SmsLength {
  readonly characters: number;
  readonly segments: number;
  readonly unicode: boolean;
}

/** Same rule as the server: 70/67 UCS-2 (Persian), 160/153 for plain ASCII. Operators may differ slightly. */
export function smsLength(text: string): SmsLength {
  const unicode = !/^[\x20-\x7E\n\r]*$/.test(text);
  const single = unicode ? 70 : 160;
  const part = unicode ? 67 : 153;
  const units = text.length;
  const segments = units === 0 ? 0 : units <= single ? 1 : Math.ceil(units / part);
  return { characters: [...text].length, segments, unicode };
}

/** Insert `{key}` into a text at the caret (or replace the selection). */
export function insertVariable(
  text: string,
  key: string,
  selectionStart: number,
  selectionEnd: number,
): { readonly text: string; readonly caret: number } {
  const token = `{${key}}`;
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  return { text: text.slice(0, start) + token + text.slice(end), caret: start + token.length };
}

/** Templates a routine may use: active, same purpose, only that routine's variables. */
export function compatibleTemplates(
  templates: readonly SmsTemplate[],
  spec: SmsRoutineSpec,
): readonly SmsTemplate[] {
  return templates.filter(
    (t) =>
      t.status === 'active' &&
      t.purpose === spec.purpose &&
      bodyVariables(t.body).every((k) => spec.variables.includes(k)),
  );
}

export function hourLabel(hour: number): string {
  return `${faNum(hour)}:۰۰`;
}

/** One human line per routine kind, e.g. «۳ روز قبل از سررسید، ۱ روز بعد از سررسید». */
export function timingSummary(settings: SmsRoutineSettings): string {
  const win = `${hourLabel(settings.sendWindow.fromHour)} تا ${hourLabel(settings.sendWindow.toHour)}`;
  let parts: string[] = [];
  switch (settings.kind) {
    case 'invoice_due':
      parts = [
        ...settings.beforeDueDays.map((d) =>
          d === 0 ? 'روز سررسید' : `${faNum(d)} روز قبل از سررسید`,
        ),
        ...settings.afterDueDays.map((d) => `${faNum(d)} روز بعد از سررسید`),
      ];
      break;
    case 'order_payment':
      parts = settings.events.map((e) => ORDER_EVENT_LABELS[e]);
      break;
    case 'followup':
      if (settings.activityRemindHours !== null)
        parts.push(`${faNum(settings.activityRemindHours)} ساعت پیش از موعد فعالیت`);
      if (settings.leadNoContactDays !== null)
        parts.push(`لید بدون تماس ${faNum(settings.leadNoContactDays)} روزه`);
      break;
    case 'occasion':
      parts = settings.occasions.map((o) => OCCASION_LABELS[o]);
      break;
  }
  return `${parts.join('، ')} — ارسال بین ${win}`;
}

export type RoutineStatus = 'unconfigured' | 'armed' | 'off';

/** `armed` = configured and switched on. It is NOT a claim that anything was sent. */
export function routineStatus(routine: SmsRoutine): RoutineStatus {
  if (!routine.configured || routine.templateId === null) return 'unconfigured';
  return routine.enabled ? 'armed' : 'off';
}
